<template>
  <b-container fluid class="contents my-4 px-3">
    <div class="p-4">
      <b-row>
        <b-col lg="2" md="4">
          <h3 class="fulfillment-form-header">
            Settings
            <i class="ic-tooltip" id="help-delivery"></i>
            <b-popover target="help-delivery"
              triggers="hover"
              content="Configure the general order fulfillment settings of your store. Change how your delivery and pick-up options are labeled in your customer channels, set-up order notifications, enable temporary store closure, and add important notes to your customers.">
            </b-popover>
          </h3>
        </b-col>
        <b-col lg="10" md="8">
          <div class="bg-white rounded p-4 form-container">
            <OrderNotification
              :selectedValue="form.order_notification"
              @setOrderNotification="handleSetOrderNotification" />
            <hr class="w-100" />
            <HomepageNotes
              :selectedValue="form.homepage_notes" />
            <hr class="w-100">
            <NotesToCustomer 
              :selectedValue="form.notes_to_customers"
              @setNotesToCustomer="handleSetNotesToCustomer"
            />
          </div>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import _ from 'lodash';
import OrderNotification from '@/components/fulfillment/OrderNotification';
import NotesToCustomer from '@/components/fulfillment/NotesToCustomer.vue';
import HomepageNotes from '@/components/fulfillment/HomepageNotes';

function initForm() {
  return {
    order_notification: {
      enable: true,
      emails: [null, null]
    },
    notes_to_customers: {
      enable: true,
      title: "",
      message: ""
    },
    homepage_notes: {
      enable: false,
      message: null
    }
  }
}

export default {
  name: "FulfillmentSettings",
  components: {
    OrderNotification,
    NotesToCustomer,
    HomepageNotes
  },
  data() {
    return {
      form: initForm()
    }
  },
  computed: {
    ...mapGetters({
      errors: 'orderFulfillment/errors',
      loading: 'orderFulfillment/loading',
      fulfillment_settings: 'orderFulfillment/fulfillment_settings'
    })
  },
  mounted() {
    this.$root.$on('handleFulfillmentSave', (origin) => {
      if (origin === 'FulfillmentSettings') this.handleSaveSettings();
    });
    this.$root.$on('saveFulfillmentChangesCancelled', (origin) => {
      if (origin === 'FulfillmentSettings') this.setFulfillmentSettings();
    });
  },
  beforeDestroy() {
    this.$root.$off('handleFulfillmentSave');
    this.$root.$off('saveFulfillmentChangesCancelled');
  },
  async created() {
    await this.getFulfillmentSettings();
    this.setFulfillmentSettings();
  },
  methods: {
    ...mapMutations({
      setErrors: 'orderFulfillment/set_errors'
    }),
    ...mapActions({
      getFulfillmentSettings: 'orderFulfillment/getFulfillmentSettings',
      saveFulfillmentSettings: 'orderFulfillment/saveFulfillmentSettings'
    }),
    isValidEmail (email) {
      const isEmail = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/; //eslint-disable-line
      return isEmail.test(email);
    },
    handleSetOrderNotification(order_notification) {
      this.form.order_notification = { ...order_notification };
    },
    handleSetNotesToCustomer(notes_to_customers) {
      this.form.notes_to_customers = { ...notes_to_customers };
    },
    async handleSaveSettings() {
      const form = this.form;
      const errors = {};

      if (form.order_notification.enable)
        form.order_notification.emails.map((email, index) => {
          if (!email) errors[`order_notification.email.${index}`] = "Email is required.";
          else if (!this.isValidEmail(email)) errors[`order_notification.email.${index}`] = "Email invalid format.";
        });
      
      if (form.notes_to_customers.enable) {
        const allowedCharacters = /[^a-z0-9;!:?.()\[\]\-_\s]/i;
        const allowedCharactersError = "Only accepts alphanumeric and special characters (semicolon, exclamation point, colon, question mark, period, parenthesis, square braces, dash, underscore)";
        const title = form.notes_to_customers.title.trim();
        const message = form.notes_to_customers.message.trim();

        if (!title) 
          errors["notes_to_customers.title"] = "Title is required.";
        if (title && allowedCharacters.test(title)) 
          errors["notes_to_customers.title"] = allowedCharactersError;
        if (!message) 
          errors["notes_to_customers.message"] = "Instruction is required.";
        if (message && allowedCharacters.test(message)) 
          errors["notes_to_customers.message"] = allowedCharactersError;
      }

      if (form.homepage_notes.enable) {
        const allowedCharacters = /[^a-z0-9;!:.()\-_&*',\s]/i;
        const allowedCharactersError = "Only accepts alphanumeric and special characters (dash, period, parenthesis, exclamation point, ampersand, asterisk, underscore, colon, semicolon, apostrophe, comma)."

        if (!form.homepage_notes.message) errors['homepage_notes.message'] = 'Message is required.';
        if (form.homepage_notes.message && allowedCharacters.test(form.homepage_notes.message)) errors['homepage_notes.message'] = allowedCharactersError;
      }

      this.setErrors(errors);
      
      if (Object.keys(errors).length === 0 || (this.errors && Object.keys(this.errors).length === 0)) {
        const { notes_to_customers, homepage_notes } = this.form;
        const payload = {
          order_notification: {
            enable: form.order_notification.enable,
            emails: form.order_notification.emails.filter(email => email && this.isValidEmail(email))
          },
          notes_to_customers,
          homepage_notes
        };

        await this.saveFulfillmentSettings(payload);
        if (this.errors && Object.keys(this.errors).length === 0) {
          this.$root.$emit('fulfillmentSavedSuccessful');
          this.getFulfillmentSettings();
        }
      }
    },
    setFulfillmentSettings() {
      const fulfillment_settings = this.fulfillment_settings;

      if (fulfillment_settings) {
        if (
          fulfillment_settings.order_notification &&
          fulfillment_settings.order_notification.constructor === Object
        ) {
          this.form.order_notification = {
            enable: fulfillment_settings.order_notification.enable,
            emails:
              fulfillment_settings.order_notification.emails.length > 0
                ? fulfillment_settings.order_notification.emails
                : [null, null],
          };
        } else this.form.order_notification = initForm().order_notification;

        if (
          fulfillment_settings.notes_to_customers &&
          fulfillment_settings.notes_to_customers.constructor === Object
        ) {
          this.form.notes_to_customers = { ...fulfillment_settings.notes_to_customers };
        } else this.form.notes_to_customers = initForm().notes_to_customers;

        if (
          fulfillment_settings.homepage_notes &&
          fulfillment_settings.homepage_notes.constructor === Object
        ) {
          this.form.homepage_notes = { ...fulfillment_settings.homepage_notes };
        } else this.form.homepage_notes = initForm().homepage_notes;
      } else {
        this.form = initForm();
      }
    }
  }
}
</script>

<style lang="scss">
@import url('../../assets/scss/fulfillment/_base.scss');
.label-type-2 {
  font-size: 22px;
}
.col-form-label.grey {
  color: #68737D;
}
#is-email-enabled .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
  background: white;
  border-radius: 10px;
  width: 10px;
  height: 10px;
  top: 7px !important;
  left: -21px !important;
}
</style>